import React, { useEffect, useMemo } from 'react'
import { makeStyles } from '@mui/styles'
import { useDispatch } from 'react-redux'

import { ModalBig } from '@/modules/base/components/modal/index'
import { getStoreLink } from '@/modules/_helpers/detectMobile'
import { setUseMobile } from '@/redux/ducks/global'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import ThemeInterface from '@/theme-interface'
import { ProductTypes, ProductTypeNames } from '@/modules/base/util/constants'
import WebstorageService from '@/modules/_services/webstorage.service'
import WebStorageKey from '@/enums/web-storage-key.enum'
import { isMeinBuero } from '@/utils/build-env'
import useDownloadContent from '@/modules/dashboard/components/app-advice-modal/useDownloadContent'
import { trackOrgamaxGaEvent } from '@/modules/base/helpers/trackOrgamaxGaEvent'
import config from 'config'
import lang from '@/lang'

import IconClose from '@assets/images/icons/v2/close.svg'
import LogoAppStore from '@assets/images/buttons/appstore.svg?url'
import LogoPlayStore from '@assets/images/buttons/playstore.svg?url'
import { useMediaBreakPoints } from '@/modules/_hooks/useMediaBreakPoints'
import { Subscription } from '@/modules/moduleSubscription/types/module'
import { ButtonSquare } from '@/modules/base/components/buttons'

const { agbUrl, imprintUrl } = config
const isMBO = isMeinBuero()

const useStyles = makeStyles((theme: ThemeInterface) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    position: 'relative',
    overflow: 'auto',
    [theme.devices.mobile]: {
      minHeight: '100%',
      zIndex: 9999,
    },
  },
  paper: {
    padding: 30,
    [theme.devices.mobile]: {
      padding: theme.spacing(2),
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    margin: '20px 0',
    [theme.devices.mobile]: {
      gap: theme.spacing(2),
      margin: `${theme.spacing(2)}px 0`,
    },

    '& p': {
      margin: 0,
    },
  },
  buttons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 20,
    marginTop: 10,
    [theme.devices.mobile]: {
      gap: theme.spacing(2),
      marginTop: theme.spacing(1),
    },
  },
  footer: {
    marginTop: 'auto',
    [theme.devices.mobile]: {
      marginTop: theme.spacing(2),
    },
  },
  footerLinks: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 20,
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    [theme.devices.mobile]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      marginTop: theme.spacing(2),
      gap: theme.spacing(1.5),
    },

    '& a': {
      color: theme.black,
    },
  },
  seperator: {
    height: 13,
    width: 1,
    backgroundColor: '#a0a4af',
    [theme.devices.mobile]: {
      display: 'none',
    },
  },
  storeLogo: {
    width: 'auto',
    [theme.devices.mobile]: {
      height: 'auto',
      maxWidth: '100%',
    },
  },
  close: {
    position: 'absolute',
    top: 0,
    right: 0,
    [theme.devices.mobile]: {
      top: theme.spacing(1),
      right: theme.spacing(1),
    },
  },
}))

interface AppAdviceModalProps {
  closeModal: () => void
  activeSubscriptions: Array<Subscription>
}

const allowedSubscriptionTypes = [ProductTypes.MAIN, ProductTypes.DOCUMENT, ProductTypes.STAFF]

export const AppAdviceModal = (props: AppAdviceModalProps) => {
  const dispatch = useDispatch()
  const classes = useStyles(props)
  const { activeSubscriptions = [], closeModal } = props
  const breakpoints = useMediaBreakPoints()

  const activeSubscriptionTypes = activeSubscriptions.map(sub => sub.typeOfSubscription)
  const primarySubscription = activeSubscriptionTypes.filter(sub => allowedSubscriptionTypes.includes(sub))?.[0]

  const { isStaff, isStaffTrial } = useMemo(() => {
    const staffSubscription = activeSubscriptions.find(sub => sub.typeOfSubscription === ProductTypes.STAFF)
    return {
      isStaff: activeSubscriptionTypes.some(sub => sub === ProductTypes.STAFF),
      isStaffTrial: staffSubscription?.isTrial,
    }
  }, [activeSubscriptions])

  const showCloseIcon = breakpoints.md || isStaff

  useEffect(() => {
    trackOrgamaxGaEvent('Mobile Hinweis', 'Öffnen', ProductTypeNames[primarySubscription])
  }, [])

  const handleContinue = () => {
    WebstorageService.setItem(WebStorageKey.OVERRIDE_MOBILE_WARNING, true)
    trackOrgamaxGaEvent('Mobile Hinweis', 'Schließen', ProductTypeNames[primarySubscription])
    dispatch(setUseMobile())
    closeModal()
  }

  const getStoreButtons = (product: typeof ProductTypes[keyof typeof ProductTypes]) => {
    const androidLink = getStoreLink('android', product)
    const iosLink = getStoreLink('ios', product)

    const openLink = (isIOS = false) => {
      if (isIOS) {
        trackOrgamaxGaEvent('Mobile Hinweis', 'Weiterleitung App Store', ProductTypeNames[primarySubscription])
        window.open(iosLink, '_blank')
      } else {
        trackOrgamaxGaEvent('Mobile Hinweis', 'Weiterleitung Play Store', ProductTypeNames[primarySubscription])
        window.open(androidLink, '_blank')
      }
    }

    return (
      <div className={classes.buttons}>
        <SVGIcon className={classes.storeLogo} svg={LogoAppStore} height={40} onClick={() => openLink(true)} />
        <SVGIcon className={classes.storeLogo} svg={LogoPlayStore} height={40} onClick={() => openLink()} />
      </div>
    )
  }

  const { getContent } = useDownloadContent({ activeSubscriptions: activeSubscriptionTypes, getStoreButtons })

  const continueButton = useMemo(() => {
    if (!isMBO || !isStaff || (isStaff && isStaffTrial)) {
      return null
    }

    return <ButtonSquare label={lang.appDeviceModal.continue} onClick={handleContinue} />
  }, [isMBO, isStaff, isStaffTrial, handleContinue])

  if (breakpoints.lg) {
    return null
  }

  return (
    <ModalBig
      fullScreen
      open={true}
      disableEscapeKeyDown
      className={classes.paper}
      width={null}
      showCloseIcon={false}
      // prevent notifications from showing up
      style={{ zIndex: 1299 }}
    >
      <div className={classes.container}>
        {!isMBO && showCloseIcon && <SVGIcon onClick={handleContinue} className={classes.close} svg={IconClose} width="16px" height="16px" />}
        <div className={classes.content}>{getContent()}</div>
        <div className={classes.footer}>
          {continueButton}
          <div className={classes.footerLinks}>
            <a href={imprintUrl} rel="noopener noreferrer" target="_blank">{lang.footer.impressum}</a><div className={classes.seperator} />
            <a href={agbUrl} rel="noopener noreferrer" target="_blank">{lang.footer.agb} & {lang.footer.legal}</a><div className={classes.seperator} />
            <a onClick={() => window.UC_UI?.showSecondLayer?.()}>{lang.appDeviceModal.cookies}</a>
          </div>
        </div>
      </div>
    </ModalBig>
  )
}

export default AppAdviceModal
